import React from "react";
import { Link } from "react-router-dom";
import {
  Phone,
  ArrowRight,
  ShieldCheck,
  HeartHandshake,
  Award,
  Sparkles,
  Quote,
} from "lucide-react";
import { BRAND, SERVICES, TESTIMONIALS, VALUES, IMAGES } from "@/lib/site";
import ServiceIcon from "@/components/site/ServiceIcon";
import ContactForm from "@/components/site/ContactForm";
import Seo from "@/components/site/Seo";
import ReviewSchema from "@/components/site/ReviewSchema";

export default function Home() {
  return (
    <div data-testid="page-home">
      <ReviewSchema />
      <Seo
        path="/"
        title="Premium In-Home Senior Care in Naples, Fort Myers & SWFL"
        description="Compassionate, concierge-level in-home senior care across Naples, Fort Myers, Cape Coral & Bonita Springs. Vetted caregivers, 24/7 availability, family-first values. Schedule a free consultation with Hortense's team."
      />
      {/* HERO */}
      <section
        data-testid="home-hero"
        className="relative overflow-hidden isolate"
      >
        <div className="absolute inset-0 -z-10">
          <img
            src={IMAGES.hero}
            alt="Caregiver assisting an elderly woman in a sunlit home"
            className="w-full h-full object-cover"
          />
          {/* Stronger multi-layer overlay for hero text legibility */}
          <div className="absolute inset-0 bg-gradient-to-r from-navy-900/95 via-navy-900/85 to-navy-900/40" />
          <div className="absolute inset-0 bg-gradient-to-b from-navy-900/50 via-transparent to-navy-900/60" />
        </div>

        <div className="container-x pt-24 md:pt-32 pb-28 md:pb-36 min-h-[calc(100vh-5rem)] flex items-center">
          <div
            className="max-w-2xl text-white animate-fade-up"
            style={{ textShadow: "0 2px 24px rgba(10,25,47,0.55)" }}
          >
            <span className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.28em] text-gold-300 font-medium mb-6">
              <span className="h-px w-8 bg-gold-300" />
              Premium in-home care • {BRAND.serviceArea}
            </span>
            <h1 className="font-serif text-4xl sm:text-5xl lg:text-[68px] leading-[1.03] tracking-tight text-white drop-shadow-[0_2px_12px_rgba(10,25,47,0.6)]">
              Compassionate, reliable care —{" "}
              <em className="text-gold-300 not-italic font-medium italic">
                right at home.
              </em>
            </h1>
            <p className="mt-7 text-lg md:text-xl text-white/95 max-w-xl leading-relaxed">
              Trusted in-home care for your loved ones, delivered with dignity,
              warmth, and concierge-level professionalism.
            </p>
            <div className="mt-10 flex flex-wrap items-center gap-4">
              <Link
                to="/contact"
                data-testid="hero-cta-consultation"
                className="btn-primary"
              >
                {BRAND.cta.primary} <ArrowRight className="h-4 w-4" />
              </Link>
              <a
                href={BRAND.phoneHref}
                data-testid="hero-cta-call"
                className="btn-ghost-light"
              >
                <Phone className="h-4 w-4" strokeWidth={1.75} />
                {BRAND.cta.callNow} • {BRAND.phone}
              </a>
            </div>

            <div className="mt-14 grid grid-cols-3 gap-6 max-w-lg">
              {[
                { n: "24/7", l: "Care availability" },
                { n: "100%", l: "Vetted caregivers" },
                { n: "SWFL", l: "Proudly serving" },
              ].map((s) => (
                <div key={s.l}>
                  <div className="font-serif text-3xl md:text-4xl text-gold-300">
                    {s.n}
                  </div>
                  <div className="mt-1 text-sm text-white/70">{s.l}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* TRUST STRIP */}
      <section className="bg-navy-50/60 border-y border-navy-900/5">
        <div className="container-x py-10 md:py-12 grid grid-cols-1 md:grid-cols-3 gap-8">
          {[
            {
              I: ShieldCheck,
              t: "Background-checked",
              s: "Every caregiver rigorously screened and trained.",
            },
            {
              I: HeartHandshake,
              t: "Family-first care",
              s: "We treat your loved ones as our own.",
            },
            {
              I: Award,
              t: "Concierge service",
              s: "One dedicated contact, consistent caregivers.",
            },
          ].map(({ I, t, s }) => (
            <div key={t} className="flex items-start gap-4">
              <span className="inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-white border border-navy-900/10 text-gold-500">
                <I className="h-5 w-5" strokeWidth={1.6} />
              </span>
              <div>
                <div className="font-serif text-xl text-navy-900">{t}</div>
                <div className="text-navy-600 mt-1 text-sm md:text-base">
                  {s}
                </div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* SERVICES */}
      <section id="services" data-testid="home-services" className="section">
        <div className="container-x">
          <div className="max-w-2xl">
            <span className="overline">Our services</span>
            <h2 className="text-4xl md:text-5xl leading-tight">
              Thoughtful care,{" "}
              <span className="italic text-gold-500">tailored daily.</span>
            </h2>
            <p className="mt-5 text-navy-600 text-lg max-w-xl">
              Every family is different. Our caregivers build a plan around
              your loved one’s routines, needs, and personality.
            </p>
          </div>

          <div className="mt-14 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {SERVICES.map((s, i) => (
              <Link
                key={s.slug}
                to={`/services/${s.slug}`}
                data-testid={`service-card-${s.slug}`}
                className="card-soft p-8 md:p-10 group block"
                style={{ animationDelay: `${i * 60}ms` }}
              >
                <div className="inline-flex h-12 w-12 items-center justify-center rounded-xl bg-navy-900 text-gold-300 group-hover:bg-gold-400 group-hover:text-navy-900 transition-colors">
                  <ServiceIcon name={s.icon} className="h-6 w-6" />
                </div>
                <h3 className="mt-6 font-serif text-2xl text-navy-900">
                  {s.title}
                </h3>
                <p className="mt-3 text-navy-600 leading-relaxed">
                  {s.description}
                </p>
                <span className="mt-5 inline-flex items-center gap-1.5 text-sm font-medium text-navy-800 group-hover:text-gold-500 transition-colors">
                  Learn more <ArrowRight className="h-3.5 w-3.5" />
                </span>
              </Link>
            ))}
          </div>

          <div className="mt-12">
            <Link
              to="/services"
              data-testid="home-services-view-all"
              className="inline-flex items-center gap-2 text-navy-900 font-medium hover:text-gold-500 transition-colors"
            >
              View all services <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        </div>
      </section>

      {/* FOUNDER STORY */}
      <section data-testid="home-founder" className="section bg-navy-50/50">
        <div className="container-x grid grid-cols-1 lg:grid-cols-2 gap-14 lg:gap-20 items-center">
          <div className="relative">
            <div className="absolute -inset-4 bg-gold-100/60 rounded-3xl -z-10" />
            <img
              src={IMAGES.founder}
              alt="Hortense, founder of Elite Home Care"
              className="w-full rounded-2xl object-cover shadow-xl"
            />
            <div className="absolute -bottom-6 -right-6 bg-navy-900 text-white rounded-2xl p-6 shadow-xl hidden md:block max-w-xs">
              <Sparkles className="h-5 w-5 text-gold-300" strokeWidth={1.6} />
              <p className="mt-3 font-serif italic text-lg leading-snug">
                “Every family deserves to feel this safe.”
              </p>
              <p className="mt-2 text-xs tracking-widest uppercase text-gold-300">
                — Hortense
              </p>
            </div>
          </div>

          <div>
            <span className="overline">Our story</span>
            <h2 className="text-4xl md:text-5xl leading-tight">
              Rooted in family.{" "}
              <span className="italic text-gold-500">Led by Hortense.</span>
            </h2>
            <p className="mt-6 text-navy-700 text-lg leading-relaxed">
              Elite Home Care was founded on a simple belief: every parent,
              grandparent, and neighbor deserves to age with dignity — in the
              home they love.
            </p>
            <p className="mt-4 text-navy-600 leading-relaxed">
              After caring for her own mother, Hortense built a team of
              caregivers who share her standards: unhurried, attentive, and
              deeply respectful. Today, that standard defines every visit.
            </p>
            <div className="mt-10 space-y-5">
              {VALUES.map((v) => (
                <div key={v.title} className="flex items-start gap-4">
                  <span className="mt-2 h-2 w-2 rounded-full bg-gold-400 shrink-0" />
                  <div>
                    <div className="font-serif text-xl text-navy-900">
                      {v.title}
                    </div>
                    <div className="text-navy-600 mt-1">{v.body}</div>
                  </div>
                </div>
              ))}
            </div>
            <Link
              to="/about"
              data-testid="home-about-link"
              className="btn-secondary mt-10"
            >
              Meet Hortense <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        </div>
      </section>

      {/* TESTIMONIALS */}
      <section
        data-testid="home-testimonials"
        className="section relative overflow-hidden"
      >
        <div
          aria-hidden
          className="absolute inset-0 -z-10 opacity-60"
          style={{
            backgroundImage: `url(${IMAGES.testimonialBg})`,
            backgroundSize: "cover",
            backgroundPosition: "center",
          }}
        />
        <div className="container-x">
          <div className="max-w-2xl">
            <span className="overline">Families we serve</span>
            <h2 className="text-4xl md:text-5xl leading-tight">
              Trusted by the families who matter most — yours.
            </h2>
          </div>

          <div className="mt-14 grid grid-cols-1 md:grid-cols-3 gap-6">
            {TESTIMONIALS.map((t, i) => (
              <figure
                key={t.name}
                data-testid={`testimonial-${i}`}
                className="card-soft p-8 md:p-9 flex flex-col"
              >
                <Quote className="h-7 w-7 text-gold-400" strokeWidth={1.2} />
                <blockquote className="mt-4 font-serif text-xl md:text-[22px] leading-snug text-navy-900">
                  “{t.quote}”
                </blockquote>
                <figcaption className="mt-6 pt-6 border-t border-navy-900/10">
                  <div className="font-medium text-navy-900">{t.name}</div>
                  <div className="text-sm text-navy-500">{t.role}</div>
                </figcaption>
              </figure>
            ))}
          </div>
        </div>
      </section>

      {/* FINAL CTA */}
      <section data-testid="home-final-cta" className="relative">
        <div className="container-x">
          <div className="relative overflow-hidden rounded-3xl bg-navy-900 text-white p-10 md:p-16 grid grid-cols-1 lg:grid-cols-[1.2fr_1fr] gap-10 items-center">
            <div
              aria-hidden
              className="absolute -top-24 -right-24 h-72 w-72 rounded-full bg-gold-400/20 blur-3xl"
            />
            <div>
              <span className="text-gold-300 text-xs uppercase tracking-[0.28em] font-medium">
                Complimentary consultation
              </span>
              <h2 className="mt-4 font-serif text-4xl md:text-5xl text-white leading-tight">
                Let’s build a care plan, together.
              </h2>
              <p className="mt-5 text-white/80 max-w-xl leading-relaxed">
                A 20-minute call with our care coordinator — no pressure, no
                obligation. We’ll listen, understand, and share exactly how we
                can help.
              </p>
              <div className="mt-8 flex flex-wrap gap-4">
                <Link
                  to="/contact"
                  data-testid="final-cta-consultation"
                  className="btn-primary"
                >
                  Schedule my consultation
                </Link>
                <a
                  href={BRAND.phoneHref}
                  data-testid="final-cta-call"
                  className="btn-ghost-light"
                >
                  <Phone className="h-4 w-4" strokeWidth={1.75} />
                  {BRAND.phone}
                </a>
              </div>
            </div>
            <div className="relative hidden lg:block">
              <img
                src={IMAGES.service2}
                alt="Elderly couple sharing a warm moment"
                className="w-full rounded-2xl object-cover aspect-[4/5]"
              />
            </div>
          </div>
        </div>
      </section>

      {/* CONTACT FORM */}
      <section id="contact" data-testid="home-contact" className="section">
        <div className="container-x grid grid-cols-1 lg:grid-cols-5 gap-12">
          <div className="lg:col-span-2">
            <span className="overline">Start the conversation</span>
            <h2 className="text-4xl md:text-5xl leading-tight">
              Speak with a<br />
              <span className="italic text-gold-500">care specialist.</span>
            </h2>
            <p className="mt-5 text-navy-600 text-lg leading-relaxed">
              Share a few details and we’ll follow up within one business day.
              Prefer to talk now?
            </p>
            <a
              href={BRAND.phoneHref}
              data-testid="home-contact-phone"
              className="inline-flex items-center gap-3 mt-6 text-navy-900 hover:text-gold-500 transition-colors"
            >
              <span className="inline-flex h-12 w-12 items-center justify-center rounded-full bg-navy-900 text-gold-300">
                <Phone className="h-5 w-5" strokeWidth={1.6} />
              </span>
              <span>
                <span className="block text-sm text-navy-500">Call us</span>
                <span className="font-serif text-2xl">{BRAND.phone}</span>
              </span>
            </a>
          </div>
          <div className="lg:col-span-3">
            <ContactForm />
          </div>
        </div>
      </section>
    </div>
  );
}
